/**
 * ## Ours (Base UI)
 * 2 managers: one for fixed-viewport toasts, one for element-anchored toasts.
 * Toast content (icon, title, description, action button) is rendered internally.
 * Types: `success | error | warning | info | loading`.
 * * `ToastProvider` — wraps the app; renders the viewport internally; `position?: ToastPosition`
 * * `ToastPosition` — `'top-left'|'top-center'|'top-right'|'bottom-left'|'bottom-center'|'bottom-right'`
 * * `toastManager` — imperative API to fire global toasts
 * * `AnchoredToastProvider` — variant for toasts anchored to a DOM element
 * * `anchoredToastManager` — imperative API for anchored toasts; requires `positionerProps.anchor`
 *
 * ## Shadcn UI (Sonner)
 * Thin wrapper around the `sonner` library. Use `toast()` from `sonner` to fire toasts.
 * * `Toaster` — single drop-in component; auto-applies current theme; accepts all `ToasterProps`
 */

'use client'

import { Toast } from '@base-ui/react/toast'
import { useEffect } from 'react'
import { createStore, useStore } from '@w/react-store'
import { withoutActWarningSync } from '@w/utils-fe'
import { buttonVariants } from './button'
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from './icon'
import { Progress } from './progress'
import { cn } from '../lib/utils'

/**
 * Imperative toasts (add/close/update/promise) are fired from background work such as sync rounds,
 * at arbitrary moments outside any test act scope. Base UI's provider applies each event as a React
 * state update, so deliver it through withoutActWarningSync: in tests it runs inside act and has
 * been applied when the manager call returns, in production it is a direct call. Delivery must stay
 * synchronous: promise() returns the handled promise the provider hands back during delivery.
 */
function createActSafeToastManager() {
  const manager = Toast.createToastManager()
  const subscribe = manager[' subscribe']
  manager[' subscribe'] = (listener) => subscribe((data) => { withoutActWarningSync(() => { listener(data) }) })
  return manager
}

const toastManager = createActSafeToastManager()
const anchoredToastManager = createActSafeToastManager()

const touchStore = createStore(false)

function useTouchDevice() {
  useEffect(() => {
    const query = window.matchMedia('(hover: none)')
    const update = () => { touchStore.update(query.matches) }
    query.addEventListener('change', update)
    update()
    return () => { query.removeEventListener('change', update) }
  }, [])
  return useStore(touchStore)
}

type SwipeDirection = 'left' | 'right' | 'up' | 'down'
type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

function getSwipeDirections(
  position: ToastPosition,
  isTouchDevice: boolean,
): SwipeDirection[] {
  const isTop = position.startsWith('top')
  const vertical: SwipeDirection = isTop ? 'up' : 'down'

  if (isTouchDevice) return ['left', 'right', vertical]

  if (position.includes('center')) return [vertical]
  if (position.includes('left')) return ['left', vertical]
  return ['right', vertical]
}

const TOAST_ICONS = {
  error: CircleAlertIcon,
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
} as const

interface ToastProviderProps extends Toast.Provider.Props {
  portalContainer?: Toast.Portal.Props['container']
  position?: ToastPosition
}

function ToastProvider({
  portalContainer,
  children,
  position = 'bottom-right',
  ...props
}: ToastProviderProps) {
  return (
    <Toast.Provider toastManager={toastManager} {...props}>
      {children}
      <Toasts position={position} portalContainer={portalContainer} />
    </Toast.Provider>
  )
}

function Toasts({ position, portalContainer }: {
  position: ToastPosition
  portalContainer?: Toast.Portal.Props['container']
}) {
  const { toasts } = Toast.useToastManager()
  const isTouchDevice = useTouchDevice()

  return (
    <Toast.Portal container={portalContainer} data-slot="toast-portal">
      <Toast.Viewport
        className={cn(
          'fixed z-50 mx-auto flex w-[calc(100%-var(--toast-inset)*2)] max-w-90 [--toast-inset:--spacing(4)] sm:[--toast-inset:--spacing(8)]',
          // Vertical positioning
          'data-[position*=top]:top-(--toast-inset)',
          'data-[position*=bottom]:bottom-(--toast-inset)',
          // Horizontal positioning
          'data-[position*=left]:left-(--toast-inset)',
          'data-[position*=right]:right-(--toast-inset)',
          'data-[position*=center]:left-1/2 data-[position*=center]:-translate-x-1/2',
        )}
        data-position={position}
        data-slot="toast-viewport"
      >
        {toasts.map((toast) => {
          const isLoading = toast.type === 'loading'
          const Icon = toast.type
            ? TOAST_ICONS[toast.type as keyof typeof TOAST_ICONS]
            : null

          return (
            <Toast.Root
              className={cn(
                'bg-surface-2 text-on-surface absolute z-[calc(9999-var(--toast-index))] h-(--toast-calc-height) w-full rounded-xl select-none [transition:transform_.5s_cubic-bezier(.22,1,.36,1),opacity_.5s,height_.15s] not-dark:bg-clip-padding',
                // Semantic colors
                'data-[type=success]:bg-surface-success data-[type=success]:text-on-surface-success',
                'data-[type=error]:bg-surface-error data-[type=error]:text-on-surface-error',
                'data-[type=warning]:bg-surface-warning data-[type=warning]:text-on-surface-warning',
                'data-[type=info]:bg-surface-info data-[type=info]:text-on-surface-info',
                // Base positioning using data-position
                'data-[position*=right]:right-0 data-[position*=right]:left-auto',
                'data-[position*=left]:right-auto data-[position*=left]:left-0',
                'data-[position*=center]:right-0 data-[position*=center]:left-0',
                'data-[position*=top]:top-0 data-[position*=top]:bottom-auto data-[position*=top]:origin-top',
                'data-[position*=bottom]:top-auto data-[position*=bottom]:bottom-0 data-[position*=bottom]:origin-bottom',
                // Gap fill for hover
                'after:absolute after:left-0 after:h-[calc(var(--toast-gap)+1px)] after:w-full',
                'data-[position*=top]:after:top-full',
                'data-[position*=bottom]:after:bottom-full',
                // Define some variables
                '[--toast-calc-height:var(--toast-frontmost-height,var(--toast-height))] [--toast-gap:--spacing(3)] [--toast-peek:--spacing(3)] [--toast-scale:calc(max(0,1-(var(--toast-index)*.1)))] [--toast-shrink:calc(1-var(--toast-scale))]',
                // Define offset-y variable
                'data-[position*=top]:[--toast-calc-offset-y:calc(var(--toast-offset-y)+var(--toast-index)*var(--toast-gap)+var(--toast-swipe-movement-y))]',
                'data-[position*=bottom]:[--toast-calc-offset-y:calc(var(--toast-offset-y)*-1+var(--toast-index)*var(--toast-gap)*-1+var(--toast-swipe-movement-y))]',
                // Default state transform
                'data-[position*=top]:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--toast-peek))+(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]',
                'data-[position*=bottom]:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--toast-peek))-(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]',
                // Limited state
                'data-limited:opacity-0',
                // Expanded state
                'data-expanded:h-(--toast-height)',
                'data-position:data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(var(--toast-calc-offset-y))]',
                // Starting and ending animations
                'data-[position*=top]:data-starting-style:transform-[translateY(calc(-100%-var(--toast-inset)))]',
                'data-[position*=bottom]:data-starting-style:transform-[translateY(calc(100%+var(--toast-inset)))]',
                'data-ending-style:opacity-0',
                // Ending animations (direction-aware)
                'data-ending-style:not-data-limited:not-data-swipe-direction:transform-[translateY(calc(100%+var(--toast-inset)))]',
                'data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]',
                'data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]',
                'data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]',
                'data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]',
                // Ending animations (expanded)
                'data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]',
                'data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]',
                'data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]',
                'data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]',
              )}
              data-position={position}
              key={toast.id}
              swipeDirection={getSwipeDirections(position, isTouchDevice)}
              toast={toast}
            >
              {isLoading && (
                <div
                  // size-10 prevents the bubble from having 48px height instead of 40px.
                  className="bg-surface-2 absolute top-1/2 left-0 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
                  data-slot="toast-icon"
                >
                  <Progress variant="circular" size="sm" />
                </div>
              )}
              {Icon && (
                <div
                  className="bg-surface-2 data-[type=success]:bg-surface-success data-[type=error]:bg-surface-error data-[type=warning]:bg-surface-warning data-[type=info]:bg-surface-info absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 rounded-full p-2"
                  data-slot="toast-icon"
                  data-type={toast.type}
                >
                  <Icon className="in-data-[type=error]:text-on-surface-error in-data-[type=info]:text-on-surface-info in-data-[type=success]:text-on-surface-success in-data-[type=warning]:text-on-surface-warning size-5" />
                </div>
              )}
              <Toast.Content className="pointer-events-auto flex items-center justify-between gap-1.5 overflow-hidden p-5 text-sm transition-opacity duration-250 data-behind:opacity-0 data-behind:not-data-expanded:pointer-events-none data-expanded:opacity-100">
                <div className="flex flex-col gap-0.5">
                  <Toast.Title
                    className="font-semibold"
                    data-slot="toast-title"
                  />
                  <Toast.Description
                    // A div, not base-ui's <p>: descriptions carry block content such as a Details disclosure.
                    render={<div />}
                    className="text-on-surface-secondary in-data-[type=success]:text-on-surface-success in-data-[type=error]:text-on-surface-error in-data-[type=warning]:text-on-surface-warning in-data-[type=info]:text-on-surface-info"
                    data-slot="toast-description"
                  />
                </div>
                {toast.actionProps && (
                  <Toast.Action
                    className={buttonVariants({ size: 'sm' })}
                    data-slot="toast-action"
                  >
                    {toast.actionProps.children}
                  </Toast.Action>
                )}
              </Toast.Content>
            </Toast.Root>
          )
        })}
      </Toast.Viewport>
    </Toast.Portal>
  )
}

function AnchoredToastProvider({ children, ...props }: Toast.Provider.Props) {
  return (
    <Toast.Provider toastManager={anchoredToastManager} {...props}>
      {children}
      <AnchoredToasts />
    </Toast.Provider>
  )
}

function AnchoredToasts() {
  const { toasts } = Toast.useToastManager()

  return (
    <Toast.Portal data-slot="toast-portal-anchored">
      <Toast.Viewport
        className="outline-none"
        data-slot="toast-viewport-anchored"
      >
        {toasts.map((toast) => {
          const isLoading = toast.type === 'loading'
          const Icon = toast.type
            ? TOAST_ICONS[toast.type as keyof typeof TOAST_ICONS]
            : null
          const positionerProps = toast.positionerProps

          if (!positionerProps?.anchor) return null
          return (
            <Toast.Positioner
              className="z-50 max-w-[min(--spacing(64),var(--available-width))]"
              data-slot="toast-positioner"
              key={toast.id}
              sideOffset={positionerProps.sideOffset ?? 4}
              toast={toast}
            >
              <Toast.Root
                className={cn(
                  'bg-surface-2 text-on-surface relative rounded-xl text-xs text-balance transition-[scale,opacity] select-none not-dark:bg-clip-padding data-ending-style:scale-98 data-ending-style:opacity-0 data-starting-style:scale-98 data-starting-style:opacity-0',
                  // Semantic colors
                  'data-[type=success]:bg-surface-success data-[type=success]:text-on-surface-success',
                  'data-[type=error]:bg-surface-error data-[type=error]:text-on-surface-error',
                  'data-[type=warning]:bg-surface-warning data-[type=warning]:text-on-surface-warning',
                  'data-[type=info]:bg-surface-info data-[type=info]:text-on-surface-info',
                )}
                data-slot="toast-popup"
                toast={toast}
              >
                {isLoading && (
                  <div
                    className="bg-surface-2 absolute top-0 left-1/2 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
                    data-slot="toast-icon"
                  >
                    <Progress variant="circular" size="sm" />
                  </div>
                )}
                {Icon && (
                  <div
                    className="bg-surface-2 data-[type=success]:bg-surface-success data-[type=error]:bg-surface-error data-[type=warning]:bg-surface-warning data-[type=info]:bg-surface-info absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full p-2"
                    data-slot="toast-icon"
                    data-type={toast.type}
                  >
                    <Icon className="in-data-[type=error]:text-on-surface-error in-data-[type=info]:text-on-surface-info in-data-[type=success]:text-on-surface-success in-data-[type=warning]:text-on-surface-warning size-5" />
                  </div>
                )}
                <Toast.Content className="pointer-events-auto flex items-center justify-between gap-1.5 overflow-hidden p-5 text-sm">
                  <div className="flex flex-col gap-0.5">
                    <Toast.Title
                      className="font-semibold"
                      data-slot="toast-title"
                    />
                    <Toast.Description
                      className="text-on-surface-secondary in-data-[type=success]:text-on-surface-success in-data-[type=error]:text-on-surface-error in-data-[type=warning]:text-on-surface-warning in-data-[type=info]:text-on-surface-info"
                      data-slot="toast-description"
                    />
                  </div>
                  {toast.actionProps && (
                    <Toast.Action
                      className={buttonVariants({ size: 'sm' })}
                      data-slot="toast-action"
                    >
                      {toast.actionProps.children}
                    </Toast.Action>
                  )}
                </Toast.Content>
              </Toast.Root>
            </Toast.Positioner>
          )
        })}
      </Toast.Viewport>
    </Toast.Portal>
  )
}

export {
  ToastProvider,
  type ToastPosition,
  toastManager,
  AnchoredToastProvider,
  anchoredToastManager,
}
